import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';

import { EzErrorCode } from '/ezlibrary/enums/EzErrorCode.js';

/**
 * Represents the status of a validation response
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-enumeration classes with:
 *      import {
 *          // ... other imported enumeration classes,
 *          EzValidationResponseStatus
 *      } from '/ezlibrary/enums/Enumerations.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other enumeration classes with:
 *      import { EzValidationResponseStatus } from '/ezlibrary/enums//EzValidationResponse.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzValidationResponseStatus extends EzEnumeration2 {
    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     * @type {EzRegistrationState}
     */
    static #ezEnumerationSingleton = null;

    /**
     * @static
     * Static Initialization
     */
    static {
        if (null == EzValidationResponseStatus.#ezEnumerationSingleton) {
            EzValidationResponseStatus.#ezEnumerationSingleton = new EzValidationResponseStatus(
                // Enum property names
                [
                    'UNKNOWN',
                    'VALID',
                    'ERROR',
                    'WARNING',
                    'ERROR_UNSUPPORTED'
                ],
                // Enum property values for each enum property name as an array
                // Enter null if the values are equal to the names
                null,
                // Optional array of additional data for eac enumeration property.
                // Enter null if not needed.
                [
                    {
                        display: 'Unknown',
                        errorCode: EzErrorCode.UNKNOWN_VALIDATION_ERROR,
                        /**
                         * @function
                         * Generates the unkhnown validation error message
                         * Template: EzErrorCode.ezEnumData(EzErrorCode.UNKNOWN_VALIDATION_ERROR).ezTextErrorMessage
                         *      .replace(
                         *          '{VALIDATION_ERROR_MESSAGE}',
                         *          EzString.stringWithTextOrDefault(
                         *              validationMessage,
                         *              'Validation Failed')))
                         * @param {string} validationMessage
                         * The validation message
                         * Default: 'Validation Failed'
                         * @returns {string}
                         */
                        message: (validationMessage = 'Validation Failed') => EzErrorCode.ezEnumData(EzErrorCode.UNKNOWN_VALIDATION_ERROR).ezTextErrorMessage
                            .replace(
                                '{VALIDATION_ERROR_MESSAGE}',
                                EzString.stringWithTextOrDefault(
                                    validationMessage,
                                    'Validation Failed'))
                    },
                    {
                        display: 'Valid',
                        errorCode: EzErrorCode.NONE,
                        /**
                         * @function
                         * Generates the validation success message
                         * Template: EzString.stringWithTextOrDefault(validationMessage, 'Validation Successful')
                         * @param {string} validationMessage
                         * The validation success message
                         * Default: 'Validation Successful'
                         * @returns {string}
                         */
                        message: (validationMessage = 'Validation Successful') => EzString.stringWithTextOrDefault(
                            validationMessage,
                            'Validation Successful')
                    },
                    {
                        display: 'Validation Error',
                        errorCode: EzErrorCode.VALIDATION_ERROR,
                        /**
                         * @function
                         * Generates the validation error message
                         * Template: EzErrorCode.ezEnumData(EzErrorCode.VALIDATION_ERROR).ezTextErrorMessage
                         *     .replace(
                         *         '{VALIDATION_ERROR_MESSAGE}',
                         *         EzString.stringWithTextOrDefault(
                         *              validationError,
                         *              'The value is not valid.'))
                         * @param {string} validationError
                         * The validation error message
                         * Default: 'The value is not valid.'
                         * @returns {string}
                         */
                        message: (validationError = 'The value is not valid.') => EzErrorCode.ezEnumData(EzErrorCode.VALIDATION_ERROR)
                            .ezTextErrorMessage
                            .replace(
                                '{VALIDATION_ERROR_MESSAGE}',
                                EzString.stringWithTextOrDefault(
                                    validationError,
                                    'The value is not valid.'))
                    },
                    {
                        display: 'Warning',
                        errorCode: EzErrorCode.VALIDATION_WARNING,
                        /**
                         * @function
                         * Generates the validation warning message
                         * Template: EzErrorCode.ezEnumData(EzErrorCode.VALIDATION_WARNING).ezTextErrorMessage
                         *      .replace(
                         *          '{VALIDATION_WARNING_MESSAGE}',
                         *          EzString.stringWithTextOrDefault(
                         *              validationWarningMessage,
                         *              'Value is not valid.')
                         * @param {string} validationWarningMessage
                         * The validation warning message
                         * Default: 'Value is not valid.'
                         * @returns {string}
                         */
                        message: (validationWarningMessage = 'Value is not valid.') => EzErrorCode.ezEnumData(EzErrorCode.VALIDATION_WARNING)
                            .ezTextErrorMessage
                            .replace('{VALIDATION_WARNING_MESSAGE}',
                                EzString.stringWithTextOrDefault(
                                    validationWarningMessage,
                                    'Value is not valid.'))
                    },
                    {
                        display: 'Unsupported',
                        errorCode: EzErrorCode.VALIDATION_UNSUPPORTED_VALUE_ERROR,
                        /**
                         * @function
                         * Generates the unsupported value error message
                         * Template: EzString.stringWithTextOrDefault(
                         *      validationMessage,
                         *      EzErrorCode.ezEnumData(EzErrorCode.VALIDATION_UNSUPPORTED_VALUE_ERROR).ezTextErrorMessage)
                         *      .replace(
                         *          '{FIELD_NAME}',
                         *          EzString.stringWithTextOrDefault(
                         *              fieldName,
                         *              '(name not provided)'))
                         *      .replace(
                         *          '{FIELD_VALUE}',
                         *          EzString.stringWithTextOrDefault(
                         *              fieldValue,
                         *              "(value not provided)"))
                         * @param {string} validationMessage
                         * The validation message template
                         * Default: EzErrorCode.ezEnumData(EzErrorCode.VALIDATION_UNSUPPORTED_VALUE_ERROR).ezTextErrorMessage
                         * @param {string} fieldName
                         * The name of the field with the unsupported value
                         * Default: '(name not provided)'
                         * @param {string} fieldValue
                         * The unsupported field value
                         * Default: "(value not provided)"
                         * @returns {string}
                         */
                        message: (validationMessage = EzErrorCode.ezEnumData(EzErrorCode.VALIDATION_UNSUPPORTED_VALUE_ERROR).ezTextErrorMessage,
                            fieldName = '(name not provided)',
                            fieldValue = "(value not provided)") => EzString.stringWithTextOrDefault(
                                validationMessage,
                                EzErrorCode.ezEnumData(EzErrorCode.VALIDATION_UNSUPPORTED_VALUE_ERROR).ezTextErrorMessage)
                                .replace(
                                    '{FIELD_NAME}',
                                    EzString.stringWithTextOrDefault(
                                        fieldName,
                                        '(name not provied)'))
                                .replace(
                                    '{FIELD_VALUE}',
                                    EzString.stringWithTextOrDefault(
                                        fieldValue,
                                        "(value not provided)"))
                    }
                ]);
        }
    }

    /**
     * @static
     * @public @method
     * Gets the display string for the provided enumValue
     * @returns {number}
     */
    static ezToErrorCode(enumValue) {
        return EzValidationResponseStatus.ezEnumData(enumValue).errorCode;
    }

    /**
     * @static
     * @public @method
     * Gets the display string for the provided enumValue
     * @returns {string}
     */
    static ezToDisplay(enumValue) {
        return EzValidationResponseStatus.ezEnumData(enumValue).display;
    }
}
